import React from 'react';
import { 
  FiCheckSquare, 
  FiCheckCircle, 
  FiClock, 
  FiXCircle, 
  FiMinusCircle, 
  FiDownload, 
  FiFileText,
  FiInfo
} from 'react-icons/fi';

import { useEquipmentPage } from '../context/EquipmentPageContext';
import { documentUrl, formatDate, patStatus, retired } from '../equipmentData';

export default function DashboardBottomGrid() {
  const { data, records, loading, error, today } = useEquipmentPage();
  const dailyChecks = (data?.checks || []).map(item => ({ name: item.checklistItem.title + ' — ' + (item.status === 'PASS' ? 'Pass' : 'Issue'), date: formatDate(item.submission.creationDate) }));
  const activeIncidents = (data?.incidents || []).filter(item => item.status !== 'COMPLETED');
  const incident = activeIncidents[0];
  const patRecords = records.filter(item => !retired(item));
  const patSummary = [
    { label: 'Pass', color: 'text-green-600', icon: <FiCheckCircle className="w-4 h-4" /> },
    { label: 'Due Soon (≤ 30 days)', color: 'text-amber-500', icon: <FiClock className="w-4 h-4" /> },
    { label: 'Overdue', color: 'text-red-500', icon: <FiXCircle className="w-4 h-4" /> },
    { label: 'Not Required', color: 'text-slate-400', icon: <FiMinusCircle className="w-4 h-4" /> },
    { label: 'Failed', color: 'text-red-500', icon: <FiXCircle className="w-4 h-4" /> },
    { label: 'Not Recorded', color: 'text-slate-400', icon: <FiMinusCircle className="w-4 h-4" /> },
  ].map(item => { const count = patRecords.filter(record => patStatus(record, today) === item.label).length;
    return { ...item, count: loading ? '…' : error ? '—' : count, percentage: patRecords.length ? Math.round(count * 1000 / patRecords.length) / 10 + '%' : '0%' };
  }).filter((item, index) => index < 4 || (!loading && !error && item.count > 0));
  const keyDocuments = [...new Map(records.filter(item => documentUrl(item.documentUrl)).map(item => [item.documentUrl, { title: item.documentName || item.name + ' document', date: 'Updated ' + formatDate(item.updatedAt), url: documentUrl(item.documentUrl) }])).values()];
  const empty = label => loading ? 'Loading…' : error ? 'Unable to load records.' : label;

  return (
    <div className="w-full bg-slate-50/30 p-1 font-sans text-slate-900">
      
      {/* Main 4-Column Grid Array */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 items-stretch mb-5">
        
        {/* CARD 1: Recent Equipment Daily Checks */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col justify-between shadow-sm">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-900">Recent Equipment Daily Checks</h3>
              <a href="/checks/equipment-check" className="text-xs font-bold text-blue-600 hover:underline">View All</a>
            </div>
            <div className="space-y-3.5">
              {!dailyChecks.length && <p className="text-xs text-slate-400">{empty('No approved equipment check results in this range.')}</p>}
              {dailyChecks.map((item, idx) => (
                <div key={idx} className="flex items-start gap-2.5">
                  <FiCheckSquare className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5 stroke-[2.5]" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-xs font-semibold text-slate-800 truncate leading-tight">{item.name}</span>
                  </div>
                  <span className="text-[11px] font-medium text-slate-400 font-mono whitespace-nowrap self-center">{item.date}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-5 pt-3 border-t border-slate-50">
            <a href="/checks/equipment-check" className="text-xs font-bold text-blue-600 hover:underline">View all daily checks</a>
          </div>
        </div>

        {/* CARD 2: PAT Summary */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col justify-between shadow-sm">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-900">PAT (Electrical Test) Summary</h3>
              <a href="/compliance-center/equipment" className="text-xs font-bold text-blue-600 hover:underline">View All</a>
            </div>
            <div className="space-y-3.5">
              {patSummary.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between gap-4 py-0.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={`${item.color} shrink-0 stroke-[2.5]`}>{item.icon}</span>
                    <span className="text-xs font-semibold text-slate-600 truncate">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs font-bold text-slate-800">{item.count}</span>
                    <span className={`text-xs font-semibold w-12 text-right ${item.color}`}>{item.percentage}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-5 pt-3 border-t border-slate-50">
            <a href="/compliance-center/equipment" className="text-xs font-bold text-blue-600 hover:underline">View all PAT records</a>
          </div>
        </div>

        {/* CARD 3: Active Incidents */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col justify-between shadow-sm">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-900">Active Incidents</h3>
              <a href="/incident" className="text-xs font-bold text-blue-600 hover:underline">View All</a>
            </div>
            
            {/* Embedded Red Flag Container block */}
            <div className="bg-red-50/40 border border-red-100/60 rounded-xl p-4">
              <div className="flex items-center justify-between gap-2 mb-2.5">
                <div className="flex items-center gap-1.5">
                  <span style={{ visibility: incident ? 'visible' : 'hidden' }} className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                  <span className="text-xs font-bold text-slate-800">{incident ? "Equipment Fault" : "Active Incidents"}</span>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-100/60 border border-red-200/40 px-1.5 py-0.5 rounded-md">
                  {incident ? incident.status.replaceAll("_", " ") : "—"}
                </span>
              </div>
              <h4 className="text-xs font-bold text-slate-800 leading-tight">{incident?.equipment || (incident ? "Equipment not recorded" : empty("No active equipment incidents."))}</h4>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{incident?.description || "—"}</p>
              <div className="text-[11px] text-slate-400 font-medium mt-3 space-y-0.5 font-mono">
                <div>Reported: {formatDate(incident?.incidentDateTime)}</div>
                <div>Reported by: {incident?.createdBy?.fullName || "—"}</div>
              </div>
            </div>
          </div>
          <div className="mt-5 pt-3 border-t border-slate-50">
            <a href="/incident" className="text-xs font-bold text-blue-600 hover:underline">View all incidents ({activeIncidents.length})</a>
          </div>
        </div>

        {/* CARD 4: Key Documents */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col justify-between shadow-sm">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-900">Key Documents</h3>
              <a href="/compliance-center/equipment" className="text-xs font-bold text-blue-600 hover:underline">Manage</a>
            </div>
            <div className="space-y-3.5">
              {!keyDocuments.length && <p className="text-xs text-slate-400">{empty('No equipment documents recorded.')}</p>}
              {keyDocuments.slice(0, 4).map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between gap-4 group">
                  <div className="flex items-start gap-2.5 min-w-0">
                    <FiFileText className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-semibold text-slate-800 truncate leading-tight group-hover:text-blue-600 transition-colors">
                        {doc.title}
                      </span>
                      <span className="text-[10px] font-medium text-slate-400 mt-0.5">{doc.date}</span>
                    </div>
                  </div>
                  <a href={doc.url} target="_blank" rel="noreferrer" aria-label={"Open " + doc.title} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-all shrink-0">
                    <FiDownload className="w-3.5 h-3.5" />
                  </a>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-5 pt-3 border-t border-slate-50">
            <a href="/compliance-center/equipment" className="text-xs font-bold text-blue-600 hover:underline">View all documents</a>
          </div>
        </div>

      </div>

      {/* Bottom Horizontal Global Notice Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border border-slate-200/80 bg-white rounded-xl p-3.5 px-4 shadow-sm">
        <div className="flex items-center gap-2 min-w-0">
          <FiInfo className="w-4 h-4 text-slate-400 shrink-0" />
          <p className="text-xs font-medium text-slate-500 truncate">
            Keep equipment records up to date to stay compliant and inspection ready.
          </p>
        </div>
        
        {/* Fast Action Document Links */}
        <div className="flex flex-wrap items-center gap-5 shrink-0 pl-6 lg:pl-0">
          <a href="/compliance-center/equipment" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors">
            <FiFileText className="w-3.5 h-3.5" />
            <span>Equipment Compliance Report</span>
          </a>
          <a href="/compliance-center/equipment" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors">
            <FiFileText className="w-3.5 h-3.5" />
            <span>Audit Pack (Equipment)</span>
          </a>
        </div>
      </div>

    </div>
  );
}
